"use client";

import { useEffect, useState } from "react";

type ContentColorType =
  | "default"
  | "gray"
  | "brown"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "purple"
  | "pink"
  | "red";

interface ContentColorProps {
  color: ContentColorType;
  variant?: "text" | "background";
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

// 텍스트 색상 - 다크모드에서도 선명하게 보이는 색상 사용
const textColorStyles: Record<ContentColorType, { light: string; dark: string }> = {
  default: { light: "#171717", dark: "#fafafa" },
  gray: { light: "#737373", dark: "#a3a3a3" },
  brown: { light: "#92400e", dark: "#fcd34d" },
  orange: { light: "#ea580c", dark: "#fdba74" },
  yellow: { light: "#ca8a04", dark: "#fef08a" },
  green: { light: "#16a34a", dark: "#86efac" },
  blue: { light: "#2563eb", dark: "#93c5fd" },
  purple: { light: "#9333ea", dark: "#c4b5fd" },
  pink: { light: "#db2777", dark: "#f9a8d4" },
  red: { light: "#dc2626", dark: "#fca5a5" },
};

// 배경 색상 - 다크모드에서 어두운 버전 사용
const bgColorStyles: Record<ContentColorType, { light: string; dark: string }> = {
  default: { light: "transparent", dark: "transparent" },
  gray: { light: "#f3f4f6", dark: "#262626" },
  brown: { light: "#fef3c7", dark: "#422006" },
  orange: { light: "#ffedd5", dark: "#431c07" },
  yellow: { light: "#fef9c3", dark: "#3f3f00" },
  green: { light: "#dcfce7", dark: "#052e16" },
  blue: { light: "#dbeafe", dark: "#172554" },
  purple: { light: "#f3e8ff", dark: "#3b0764" },
  pink: { light: "#fce7f3", dark: "#500724" },
  red: { light: "#fee2e2", dark: "#450a0a" },
};

const sizeStyles: Record<"sm" | "md" | "lg", string> = {
  sm: "w-4 h-4 text-[10px]",
  md: "w-5 h-5 text-xs",
  lg: "w-6 h-6 text-sm",
};

const colorLabels: Record<ContentColorType, string> = {
  default: "기본",
  gray: "회색",
  brown: "갈색",
  orange: "주황",
  yellow: "노랑",
  green: "초록",
  blue: "파랑",
  purple: "보라",
  pink: "분홍",
  red: "빨강",
};

export function ContentColor({
  color,
  variant = "text",
  size = "md",
  showLabel = false,
  className = "",
}: ContentColorProps) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // 초기 다크모드 상태 확인
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsDark(document.documentElement.classList.contains("dark"));

    // 다크모드 변경 감지
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          setIsDark(document.documentElement.classList.contains("dark"));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const isText = variant === "text";
  const mode = isDark ? "dark" : "light";

  // 텍스트 variant: 텍스트 색상만 변경, 배경은 투명
  // 배경 variant: 배경 색상 변경, 텍스트는 그에 맞는 대비 색상
  const textColor = isText
    ? textColorStyles[color][mode]
    : textColorStyles.default[mode];
  const bgColor = isText
    ? bgColorStyles.default[mode]
    : bgColorStyles[color][mode];

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span
        className={`
          rounded border border-[var(--color-border)] flex items-center justify-center font-bold
          ${sizeStyles[size]}
        `}
        style={{
          backgroundColor: bgColor,
          color: textColor,
        }}
      >
        A
      </span>
      {showLabel && (
        <span className="text-sm text-[var(--color-text-secondary)]">{colorLabels[color]}</span>
      )}
    </div>
  );
}

export { type ContentColorProps, type ContentColorType };
